import { fadeUp } from '@/lib/animations';
import { formatDate } from '@/lib/format';
import type { MobileLine } from '@octave/contracts';
import { motion } from 'framer-motion';
import { Clock, Loader2, X } from 'lucide-react';
import {
  canCancelTermination,
  getStatusAlertConfig,
  isLineActionsDisabled,
} from './helpers';

interface LineStatusCalloutsProps {
  lineDetails: MobileLine;
  isReactivating: boolean;
  onCancelTermination: () => void;
  onCancelPlanChange: () => void;
}

/** Status alert + upcoming plan change callouts shown above the hero card. */
export function LineStatusCallouts({
  lineDetails,
  isReactivating,
  onCancelTermination,
  onCancelPlanChange,
}: LineStatusCalloutsProps) {
  const alertConfig = getStatusAlertConfig(lineDetails.status);

  return (
    <>
      {alertConfig && (
        <motion.div
          variants={fadeUp}
          className={`flex items-center justify-between rounded-2xl px-6 py-4 ${alertConfig.bg}`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-xl ${alertConfig.iconBg}`}
            >
              <alertConfig.icon className={`h-5 w-5 ${alertConfig.iconColor}`} />
            </div>
            <div>
              <p className={`text-sm font-semibold ${alertConfig.titleColor}`}>
                {alertConfig.title}
              </p>
              <p className={`text-xs ${alertConfig.descColor}`}>
                {alertConfig.description}
              </p>
            </div>
          </div>
          {canCancelTermination(lineDetails.status) && (
            <button
              onClick={onCancelTermination}
              disabled={isReactivating}
              className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:shadow disabled:opacity-50"
            >
              {isReactivating && (
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
              )}
              Annuler la résiliation
            </button>
          )}
        </motion.div>
      )}

      {lineDetails.upcomingPhonePlan && (
        <motion.div
          variants={fadeUp}
          className="flex items-center justify-between rounded-2xl bg-sky-50 px-6 py-4"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100">
              <Clock className="h-5 w-5 text-sky-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-sky-800">
                Changement de forfait programmé
              </p>
              <p className="text-xs text-sky-600">
                Le forfait sera changé pour "{lineDetails.upcomingPhonePlan.name}
                " le {formatDate(lineDetails.upcomingPhonePlan.date)}.
              </p>
            </div>
          </div>
          <button
            onClick={onCancelPlanChange}
            disabled={isLineActionsDisabled(lineDetails.status)}
            className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:shadow disabled:opacity-50"
          >
            <X className="mr-1.5 inline h-3.5 w-3.5" />
            Annuler
          </button>
        </motion.div>
      )}
    </>
  );
}
